import { Mode, ps4Mode, ps5Mode } from '../../common/types';
import {
  addGlobalContexts,
  BrowserTracker,
  newTracker,
  trackPageView as snowplowTrackPageView,
} from '@snowplow/browser-tracker';
import { snowplowAnalyticsProperties } from '../environment';
import {
  endMediaTracking,
  SnowplowMediaPlugin,
  trackMediaEnd,
  trackMediaSelfDescribingEvent,
} from '@snowplow/browser-plugin-media';
import { HbbTVPlayer } from '../Player/adapters/HbbTVPlayer';
import { Html5Player } from '../Player/adapters/HTML5Player';
import {
  activeEntityContextGenerator,
  AnalyticsContext,
  ContextContentLanguage,
  deviceContextGenerator,
  mediaTrackingGenerator,
  yleContextGenerator,
} from './snowplowContexts';
import { Language } from '@yleisradio/areena-types';
import { AudioItem, Device, SubtitleItem } from '../appTypings';
import { uiLanguageToSnowplowLanguage } from './snowplowUtils';
import { isOldPanasonic } from '../utils/deviceCheck';

let tracker: BrowserTracker | null | undefined;

export function getTracker(mode: Mode, device: Device | undefined) {
  const { getAppId, collectorUrl, trackerNamespace } =
    snowplowAnalyticsProperties;

  const appId = getAppId(modeToManufacturer(mode));
  const stateStorageStrategy = isOldPanasonic(device) ? 'cookie' : undefined;

  if (!tracker) {
    tracker = newTracker(trackerNamespace, collectorUrl, {
      appId,
      appVersion: window.yleLeanback.VERSION,
      bufferSize: 1,
      discoverRootDomain: true,
      cookieSameSite: 'Lax',
      contexts: {
        browser: true,
        session: true,
      },
      maxLocalStorageQueueSize: 100, // Decreased from 1000 to avoid QuotaExceededError
      stateStorageStrategy,
      platform: mode === ps4Mode || mode === ps5Mode ? 'cnsl' : 'tv',
      plugins: [SnowplowMediaPlugin()],
    });
  }

  const deviceContext = deviceContextGenerator(device, mode);

  addGlobalContexts({
    deviceContext,
  });

  return {
    tracker,
    isSnowplowRunning: !!tracker,
  };
}

type TrackPageViewParams = {
  areena_api_yle_id?: string;
  areena_api_view_type: string | undefined;
  version: string | undefined;
  areenaApiUri: string | undefined;
  languageCode: Language;
  areena_api_entity_type: string | undefined;
};

export function trackPageView({
  areena_api_yle_id,
  areena_api_view_type,
  version,
  areenaApiUri,
  languageCode,
  areena_api_entity_type,
}: TrackPageViewParams) {
  const activeEntity = activeEntityContextGenerator({
    areena_api_yle_id,
    areena_api_view_type,
    areenaApiUri,
    version,
    areena_api_entity_type,
  });

  if (activeEntity) {
    addGlobalContexts({
      activeEntity,
    });
  }

  const yleContext = yleContextGenerator({
    service_language: uiLanguageToSnowplowLanguage(languageCode),
    content_language: uiLanguageToSnowplowLanguage(languageCode),
    section: areena_api_view_type,
    context_index: 1,
  });

  if (yleContext) {
    addGlobalContexts({
      yleContext,
    });
  }

  snowplowTrackPageView();
}

function modeToManufacturer(mode: Mode) {
  switch (mode) {
    case 'tizen':
      return 'samsung';
    case 'webos':
      return 'lg';
    default:
      return mode;
  }
}

enum MediaType {
  Audio = 'audio',
  Video = 'video',
}

export type getMediaTrackingConfigurationProps = {
  mediaSessionId: string;
  player: HbbTVPlayer | Html5Player | undefined;
  isLiveMedia: boolean;
  isAudio: boolean;
  isPaused: boolean;
  label: string;
  programId: string;
  dvr_window: string | undefined;
  publication_id: string | undefined;
  section: string | undefined;
  content_language: ContextContentLanguage;
  service_language: Language;
  service_id: string | undefined;
  media_id: string;
};

export type MediaTrackingConfiguration = {
  id: string;
  player: {
    currentTime: number;
    duration: number;
    livestream: boolean;
    label: string;
    mediaType: MediaType;
    paused: boolean;
  };
  context: AnalyticsContext[];
};

export const getMediaTrackingConfiguration = ({
  programId,
  mediaSessionId,
  player,
  dvr_window,
  isLiveMedia,
  isAudio,
  isPaused,
  label,
  publication_id,
  section,
  content_language,
  service_language,
  service_id,
  media_id,
}: getMediaTrackingConfigurationProps): MediaTrackingConfiguration => {
  const config = {
    id: mediaSessionId,
    pings: { pingInterval: 30 },
    player: {
      currentTime: player?.getPosition() || 0,
      duration: player?.getDuration() || 0,
      livestream: isLiveMedia,
      label,
      mediaType: isAudio ? MediaType.Audio : MediaType.Video,
      paused: isPaused,
    },
    context: mediaTrackingGenerator({
      section,
      content_language,
      service_language,
      programId,
      isLiveMedia,
      dvr_window,
      service_id,
      media_id,
      publication_id,
    }),
  };

  return config;
};

export const onPlayerClose = (mediaSessionId: string) => {
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      schema:
        'iglu:com.snowplowanalytics.snowplow.media/ping_event/jsonschema/1-0-0',
      data: {},
    },
  });
  endMediaTracking({ id: mediaSessionId });
};

export const onMediaEnd = (mediaSessionId: string) => {
  trackMediaEnd({ id: mediaSessionId });
};

export const onSubtitleChange = (
  mediaSessionId: string,
  selectedSubtitle?: SubtitleItem
) =>
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      data: {
        kind: selectedSubtitle?.kind.toLowerCase() ?? null,
        language_code: selectedSubtitle?.language ?? null,
        type: selectedSubtitle?.type.toLocaleLowerCase() ?? null,
      },
      schema: 'iglu:fi.yle.player/caption_track/jsonschema/2-0-1',
    },
  });

export const onAudioChange = (
  mediaSessionId: string,
  selectedAudio?: AudioItem
) =>
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      data: {
        format: selectedAudio?.format ?? null,
        language_code: selectedAudio?.language ?? null,
        type: selectedAudio?.kind.toLowerCase() ?? null,
      },
      schema: 'iglu:fi.yle.player/audio_track/jsonschema/2-0-1',
    },
  });
